'use client'

import { useEffect, useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { GridViewIcon } from '@hugeicons-pro/core-solid-rounded'
import { ArrowDown01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export type ChannelFilterValue = 'all' | 'search' | 'social' | 'tv' | 'programmatic' | 'audio' | 'retail'

export interface ChannelFilterOption {
  value: ChannelFilterValue
  label: string
  /** True if the user has at least one live connector on this channel. */
  active: boolean
}

export interface ChannelFilterProps {
  value: ChannelFilterValue
  onChange: (value: ChannelFilterValue) => void
  /** Options to show, beyond 'All channels'. */
  options: ChannelFilterOption[]
  className?: string
  size?: 'sm' | 'md'
}

const STORAGE_KEY = 'monarch.channelFilter'

export function ChannelFilter({
  value,
  onChange,
  options,
  className,
  size = 'sm',
}: ChannelFilterProps) {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored && stored !== value) {
      if (stored === 'all' || options.some((option) => option.value === stored && option.active)) {
        onChange(stored as ChannelFilterValue)
      }
    }
    // Restore once on mount; parent state owns subsequent changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleChange(next: ChannelFilterValue) {
    sessionStorage.setItem(STORAGE_KEY, next)
    onChange(next)
    setOpen(false)
  }

  const selectedLabel = value === 'all'
    ? 'All channels'
    : options.find((option) => option.value === value)?.label ?? 'All channels'

  const triggerClassName = cn(
    'gap-1 sm:gap-1.5 text-xs font-medium min-h-[44px] sm:min-h-0',
    size === 'md' ? 'h-11 sm:h-9 px-2.5 sm:px-3' : 'h-11 sm:h-7 px-1.5 sm:px-2',
    className,
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className={triggerClassName}>
          <HugeiconsIcon icon={GridViewIcon} className="size-3.5 shrink-0 text-atrium-black/60 dark:text-atrium-black/70" />
          <span className="max-w-[120px] truncate sm:max-w-none">{selectedLabel}</span>
          <HugeiconsIcon icon={ArrowDown01Icon} className="size-3.5 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-1" align="end">
        <button
          type="button"
          onClick={() => handleChange('all')}
          className={cn(
            'w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-surface-0 dark:hover:bg-canvas',
            value === 'all' && 'bg-accent-50 font-medium dark:bg-accent-950',
          )}
        >
          All channels
        </button>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={!option.active}
            onClick={() => handleChange(option.value)}
            className={cn(
              'w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-surface-0 dark:hover:bg-canvas',
              value === option.value && 'bg-accent-50 font-medium dark:bg-accent-950',
              !option.active && 'cursor-not-allowed opacity-50',
            )}
          >
            {option.label}
            {!option.active && <span className="ml-2 text-text-muted-2">(no live connectors)</span>}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}
